import React, { useState, useEffect } from 'react';
import api from '../api'; 
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import PageLayout from '../components/PageLayout'; // IMPORT THE LAYOUT

const ProfilePage = () => {
    const { user, loading } = useAuth(); 
    const { navigateTo } = useNavigation();
    
    // --- CLASS HISTORY STATE ---
    const [completedClasses, setCompletedClasses] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [error, setError] = useState('');

    // --- PASSWORD CHANGE STATE ---
    const [isEditing, setIsEditing] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [passMessage, setPassMessage] = useState('');
    const [passLoading, setPassLoading] = useState(false);

    // --- HELPER: DATE FORMATTING ---
    const formatDateTime = (classTime, endTime) => {
        const start = new Date(classTime);
        const end = new Date(endTime);
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'N/A';
        const optionsDate = { year: 'numeric', month: 'short', day: 'numeric' };
        const optionsTime = { hour: '2-digit', minute: '2-digit', hour12: true };
        return `${start.toLocaleString('en-AU', optionsDate)}, ${start.toLocaleString('en-AU', optionsTime)} - ${end.toLocaleString('en-AU', optionsTime)}`;
    };

    // --- ACTION: UPDATE PASSWORD ---
    const handlePasswordUpdate = async () => {
        if (!newPassword || newPassword.length < 6) {
            setPassMessage('⚠️ Password must be at least 6 characters');
            return;
        }
        setPassLoading(true);
        try {
            await api.put(`/users/${user.id || user.uid || user.ID}`, { 
                password: newPassword 
            });
            setPassMessage('✅ Password updated successfully!');
            setNewPassword('');
            setTimeout(() => {
                setIsEditing(false); 
                setPassMessage('');
            }, 2000);
        } catch (err) {
            console.error(err);
            setPassMessage('❌ Failed to update password.');
        } finally {
            setPassLoading(false);
        }
    };

    // --- LOAD CLASS HISTORY ---
    useEffect(() => {
        if (!user) return;
        if (user.role === 'student') {
            const fetchCompletedClasses = async () => {
                setDataLoading(true);
                setError('');
                try {
                    const response = await api.get(`/schedule/student/${user.id || user.uid}/completed`);
                    setCompletedClasses(response.data);
                } catch (err) {
                    if (err.response && err.response.status !== 404) {
                        setError('Failed to load class history.');
                    }
                } finally {
                    setDataLoading(false);
                }
            };
            fetchCompletedClasses();
        }
    }, [user]);

    if (loading) return <div className="text-center p-10">Checking Session...</div>;
    if (!user) return <div className="text-center p-10">Please login to view your profile.</div>;

    return (
        <PageLayout>
            <h2 className="text-3xl font-semibold mb-6 text-gray-800">My Profile</h2>
            
            {/* USER INFO CARD */}
            <div className="bg-white/90 backdrop-blur p-8 rounded-xl shadow-xl max-w-lg mx-auto space-y-4 mb-8 border-t-4 border-blue-500">
                <p className="text-lg"><span className="font-semibold text-gray-600">Name:</span> {user.name}</p>
                <p className="text-lg"><span className="font-semibold text-gray-600">Username:</span> {user.username || user.email}</p>
                <p className="text-lg capitalize"><span className="font-semibold text-gray-600">Role:</span> {user.role}</p>
                
                {user.role === 'student' && (
                    <p className="text-xl mt-4 p-4 bg-blue-50 rounded-lg text-center">
                        <span className="font-semibold text-gray-600 block text-sm uppercase">Lesson Credits</span>
                        <span className="font-black text-4xl text-blue-600">{user.lessonCredits || 0}</span>
                    </p>
                )}
                
                {/* PASSWORD CHANGE SECTION */}
                <div className="pt-6 mt-4 border-t border-gray-100">
                    {!isEditing ? (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="w-full bg-orange-100 hover:bg-orange-200 text-orange-700 font-bold py-3 px-4 rounded-lg shadow-sm transition-all"
                        >
                            Change My Password
                        </button>
                    ) : (
                        <div className="bg-orange-50 p-4 rounded-lg animate-fade-in-up">
                            <label className="text-xs font-bold text-orange-800 uppercase">New Password</label>
                            <input 
                                type="password" 
                                className="w-full p-2 mt-1 border border-orange-200 rounded focus:outline-none focus:border-orange-500"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Type new password..."
                            />
                            
                            {passMessage && <p className="text-sm font-bold mt-2 text-center">{passMessage}</p>}

                            <div className="flex gap-2 mt-3">
                                <button 
                                    onClick={() => setIsEditing(false)}
                                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 rounded"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={handlePasswordUpdate}
                                    disabled={passLoading}
                                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 rounded"
                                >
                                    {passLoading ? 'Saving...' : 'Save New Password'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* CLASS HISTORY (Student Only) */}
            {user.role === 'student' && (
                <div className="max-w-2xl mx-auto mt-8">
                    <h3 className="text-2xl font-semibold mb-4 text-gray-700">Class History</h3>
                    
                    {dataLoading && <p className="text-center text-gray-500">Loading history...</p>}
                    {error && <p className="text-center text-red-500">{error}</p>}
                    
                    {!dataLoading && completedClasses.length === 0 ? (
                        <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-200">
                            <p className="text-gray-500">No completed classes found.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {completedClasses.map((item) => (
                                <div key={item.id} className="p-5 rounded-lg shadow-md bg-white border-l-4 border-green-500 flex flex-col md:flex-row justify-between items-start md:items-center">
                                    <div>
                                        <h4 className="text-lg font-bold text-gray-800">{item.className || 'ESL Class'}</h4>
                                        <p className="text-sm text-gray-600">
                                            {formatDateTime(item.classTime, item.endTime)}
                                        </p>
                                    </div>
                                    <span className="mt-2 md:mt-0 px-3 py-1 bg-green-100 text-green-800 text-xs font-bold rounded-full uppercase">
                                        {item.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </PageLayout>
    );
};

export default ProfilePage;